import React from "react";
import { Pressable, ScrollView, StyleSheet, Text, View } from "react-native";
import { NativeStackScreenProps } from "@react-navigation/native-stack";

import { DivineBackground } from "../../shared/ui/DivineBackground";
import type { AuthStackParamList } from "../../navigation/navigationTypes";

type Props = NativeStackScreenProps<AuthStackParamList, "VersionDetails">;

const formatDate = (value: string) => {
  const parsed = new Date(value);

  if (Number.isNaN(parsed.getTime())) {
    return value;
  }

  return parsed.toLocaleString();
};

export function VersionDetailsScreen({ navigation, route }: Props) {
  const { versionNumber, title, description, createdDateTime } = route.params;

  return (
    <DivineBackground overlayColor="#FFF8E7" overlayOpacity={0.9}>
      <ScrollView contentContainerStyle={styles.container} showsVerticalScrollIndicator={false}>
        <Pressable style={styles.backButton} onPress={() => navigation.goBack()}>
          <Text style={styles.backButtonText}>Back</Text>
        </Pressable>

        <View style={styles.card}>
          <Text style={styles.caption}>App Version</Text>
          <Text style={styles.versionText}>{versionNumber}</Text>
          <Text style={styles.title}>{title?.trim() || "Version Details"}</Text>
          <Text style={styles.description}>
            {description?.trim() || "No release description available for this version."}
          </Text>

          <View style={styles.metaBlock}>
            <Text style={styles.metaLabel}>Released On</Text>
            <Text style={styles.metaValue}>{formatDate(createdDateTime)}</Text>
          </View>
        </View>
      </ScrollView>
    </DivineBackground>
  );
}

const styles = StyleSheet.create({
  container: {
    flexGrow: 1,
    paddingHorizontal: 20,
    paddingTop: 28,
    paddingBottom: 80,
  },
  backButton: {
    alignSelf: "flex-start",
    borderRadius: 999,
    borderWidth: 1,
    borderColor: "rgba(194,65,12,0.35)",
    backgroundColor: "rgba(255,255,255,0.72)",
    paddingHorizontal: 16,
    paddingVertical: 10,
    marginBottom: 18,
  },
  backButtonText: {
    color: "#9A3412",
    fontSize: 14,
    fontWeight: "700",
  },
  card: {
    borderRadius: 28,
    paddingHorizontal: 22,
    paddingVertical: 26,
    backgroundColor: "rgba(255,253,248,0.94)",
    borderWidth: 1,
    borderColor: "rgba(194,65,12,0.12)",
  },
  caption: {
    color: "#9A3412",
    fontSize: 12,
    fontWeight: "700",
    letterSpacing: 1.1,
    textTransform: "uppercase",
    marginBottom: 8,
  },
  versionText: {
    color: "#431407",
    fontSize: 28,
    fontWeight: "800",
    marginBottom: 14,
  },
  title: {
    color: "#4A1C12",
    fontSize: 22,
    fontWeight: "800",
    marginBottom: 12,
  },
  description: {
    color: "#6A4A3C",
    fontSize: 15,
    lineHeight: 24,
  },
  metaBlock: {
    marginTop: 22,
    paddingTop: 18,
    borderTopWidth: 1,
    borderTopColor: "rgba(194,65,12,0.12)",
  },
  metaLabel: {
    color: "#9A3412",
    fontSize: 12,
    fontWeight: "700",
    marginBottom: 6,
    textTransform: "uppercase",
  },
  metaValue: {
    color: "#4A1C12",
    fontSize: 15,
    fontWeight: "600",
  },
});
